import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, X, Save, Wifi, Tv, Wind, Waves, Car, Coffee, Utensils, Dumbbell, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuthState } from "@/hooks/useAuthState";
import { PropertyService } from "@/services/propertyService";
import { PropertyPhotoService } from "@/services/propertyPhotoService";
import { UploadService } from "@/services/uploadService";
import { DevelopmentService, Development } from "@/services/developmentService";
import { Property, PropertyPhoto } from "@/integrations/mysql/types";
import AdminLayout from "@/layouts/AdminLayout";
import { getPropertyImageUrl } from "@/config/api";
import RichTextEditor from "@/components/RichTextEditor";

const amenidades = [
  { id: "Wifi", label: "Wifi", icon: Wifi },
  { id: "TV", label: "TV", icon: Tv },
  { id: "Piscina Compartilhada", label: "Piscina Compartilhada", icon: Waves },
  { id: "Estacionamento", label: "Estacionamento", icon: Car },
  { id: "Ar Condicionado", label: "Ar Condicionado", icon: Wind },
  { id: "Acesso a Praia", label: "Acesso a Praia", icon: Waves },
  { id: "Academia Compartilhada", label: "Academia Compartilhada", icon: Dumbbell },
  { id: "Parque Infantil ao Ar Livre", label: "Parque Infantil ao Ar Livre", icon: Utensils },
];

const AdminEditProperty = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, userType, user, userId, loading: authLoading } = useAuthState();
  
  const [fotos, setFotos] = useState<Array<{ id: number | null; url: string; legenda: string; filename: string; file?: File }>>([]);
  const [imagemCapa, setImagemCapa] = useState<string>('');
  const [amenidadesSelecionadas, setAmenidadesSelecionadas] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [propertyData, setPropertyData] = useState<Property | null>(null);
  const [developments, setDevelopments] = useState<Development[]>([]);
  
  // Estados para os campos do formulário
  const [formData, setFormData] = useState({
    development_id: '',
    title: '',
    description: '',
    max_guests: '',
    bedrooms: '',
    bathrooms: '',
    property_type: 'apartment',
    house_rules: '',
    additional_info: '<p><strong>Lazer e Relaxamento:</strong></p><ul><li>Piscina</li><li>Deck</li><li>Deck Molhado</li><li>Palco Aquático</li><li>Hidromassagem</li><li>Prainha</li><li>Piscina Infantil</li><li>Toboágua</li><li>Piscina com Borda Infinita</li><li>Bangalôs</li><li>Lounge</li><li>Prainha Infantil</li><li>Sauna</li><li>SPA (Segundo andar)</li><li>Redário</li></ul><p><strong>Esportes e Atividades:</strong></p><ul><li>Quadra de Areia</li><li>Quadra Poliesportiva</li><li>Campo de Mini Golfe</li><li>Quadra de Tênis</li><li>Espaço Fitness (Segundo andar)</li></ul><p><strong>Alimentação e Conveniência:</strong></p><ul><li>Restaurante</li><li>Varanda do Restaurante</li><li>Bar</li><li>Restaurante Privativo</li><li>GAV Market</li><li>Pâtisserie</li><li>Sushi Lounge (Primeiro andar)</li></ul><p><strong>Infraestrutura e Serviços:</strong></p><ul><li>Estacionamento</li><li>Recepção</li><li>Recepção VIP</li><li>Chafariz</li><li>Brinquedoteca</li><li>Salão de Jogos</li><li>Espaço Família</li><li>Karaokê</li><li>Cinema</li><li>Dog Care</li><li>Sala de Beleza (Segundo andar)</li></ul>',
    is_active: true
  });

  // Verificar autenticação (AdminLayout já faz isso)

  // Carregar lista de empreendimentos
  useEffect(() => {
    const loadDevelopments = async () => {
      try {
        const allDevelopments = await DevelopmentService.getAllDevelopmentsForAdmin();
        setDevelopments(allDevelopments);
      } catch (error) {
        console.error('Erro ao carregar empreendimentos:', error);
      }
    };
    loadDevelopments();
  }, []);

  // Carregar dados do imóvel
  useEffect(() => {
    const loadProperty = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // Carregar dados reais do imóvel
        console.log('🔍 Carregando imóvel para edição, ID:', id);
        const property = await PropertyService.getPropertyByIdAnyStatus(parseInt(id));
        console.log('🏠 Imóvel carregado:', property);
        
        if (!property) {
          console.log('❌ Imóvel não encontrado');
          toast({
            title: "Erro",
            description: "Imóvel não encontrado",
            variant: "destructive",
          });
          navigate('/admin/imoveis');
          return;
        }
        
        setPropertyData(property);
        setFormData({
          development_id: property.development_id ? property.development_id.toString() : '',
          title: property.title,
          description: property.description,
          max_guests: property.max_guests.toString(),
          bedrooms: property.bedrooms.toString(),
          bathrooms: property.bathrooms.toString(),
          property_type: property.property_type,
          house_rules: property.house_rules || '',
          additional_info: property.additional_info || '',
          is_active: property.is_active
        });
        
        // Carregar amenidades
        if (property.amenities) {
          console.log('🏠 Amenidades carregadas:', property.amenities);
          if (Array.isArray(property.amenities)) {
            setAmenidadesSelecionadas(property.amenities);
          } else {
            // Se for string, tentar fazer parse
            try {
              const amenitiesArray = JSON.parse(property.amenities);
              setAmenidadesSelecionadas(amenitiesArray);
            } catch (error) {
              console.error('Erro ao parsear amenidades:', error);
              setAmenidadesSelecionadas([]);
            }
          }
        }
        
        // Carregar fotos existentes do banco de dados
        console.log('📸 Carregando fotos do imóvel...');
        const photos = await PropertyPhotoService.getActivePhotosByPropertyId(parseInt(id));
        console.log('📸 Fotos encontradas:', photos);
        
        const fotosComUrls = photos.map(photo => ({
          id: photo.id,
          url: getPropertyImageUrl(id, photo.filename),
          legenda: photo.caption || '',
          filename: photo.filename
        }));
        console.log('📸 Fotos com URLs:', fotosComUrls);
        
        setFotos(fotosComUrls);
        
        // Definir imagem de capa
        if (property.cover_image) {
          setImagemCapa(property.cover_image);
        } else if (photos.length > 0) {
          setImagemCapa(photos[0].filename);
        }
        
      } catch (error) {
        console.error('Erro ao carregar imóvel:', error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os dados do imóvel",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadProperty();
  }, [id, toast]);


  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar campos obrigatórios
    if (!formData.title.trim()) {
      toast({
        title: "Título Obrigatório",
        description: "Por favor, preencha o nome do empreendimento.",
        variant: "destructive",
      });
      return;
    }
    
    // Validar descrição removendo tags HTML e verificando se há conteúdo real
    const descriptionText = formData.description.replace(/<[^>]*>/g, '').trim();
    if (!descriptionText || descriptionText === '') {
      toast({
        title: "Descrição Obrigatória",
        description: "Por favor, preencha a descrição do imóvel.",
        variant: "destructive",
      });
      return;
    }
    
    if (!imagemCapa || imagemCapa.trim() === '') {
      toast({
        title: "Imagem de Capa Obrigatória",
        description: "Por favor, selecione uma imagem de capa para o imóvel.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setLoading(true);
      
      console.log('💾 Salvando imóvel...', { formData, amenidadesSelecionadas, imagemCapa });
      
      // Preparar dados para atualização
      const updateData = {
        development_id: formData.development_id ? parseInt(formData.development_id) : null,
        title: formData.title,
        description: formData.description,
        max_guests: formData.max_guests ? parseInt(formData.max_guests) : 1,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : 1,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : 1,
        property_type: formData.property_type,
        house_rules: formData.house_rules,
        additional_info: formData.additional_info,
        is_active: formData.is_active,
        amenities: amenidadesSelecionadas,
        cover_image: imagemCapa
      };
      
      console.log('📝 Dados para atualização:', updateData);
      
      // Atualizar imóvel no banco de dados
      await PropertyService.updateProperty(parseInt(id!), updateData);
      
      // Salvar fotos se houver novas e atualizar legendas das existentes
      if (fotos.length > 0) {
        console.log('📸 Salvando fotos...', fotos);
        for (const foto of fotos) {
          if (foto.file) {
            // Upload da nova foto
            await PropertyPhotoService.uploadPhoto(parseInt(id!), foto.file, foto.legenda);
          } else if (foto.id && foto.legenda !== undefined) {
            // Atualizar legenda da foto existente
            console.log('📝 Atualizando legenda da foto ID:', foto.id, 'Legenda:', foto.legenda);
            await PropertyPhotoService.updatePhotoCaption(foto.id, foto.legenda);
          }
        }
      }
      
      toast({
        title: "Sucesso",
        description: "Imóvel atualizado com sucesso!",
      });
      
      navigate('/minha-area/admin/imoveis');
    } catch (error) {
      console.error('Erro ao salvar imóvel:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar as alterações",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (files: FileList | null) => {
    if (!files) return;
    
    Array.from(files).forEach((file) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          setFotos(prev => {
            const newFotos = [...prev, { id: null, url: result, legenda: '', filename: file.name, file }];
            return newFotos;
          });
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const removeFoto = async (index: number) => {
    const foto = fotos[index];
    const fotoRemovida = fotos[index];
    
    // Verificar se é a foto de capa
    const isFotoCapa = imagemCapa === foto.filename;
    
    // Se for a foto de capa e só restar uma foto, não permitir exclusão
    if (isFotoCapa && fotos.length === 1) {
      toast({
        title: "Não é possível excluir",
        description: "Esta é a única foto do imóvel e é obrigatória como foto de capa.",
        variant: "destructive",
      });
      return;
    }
    
    if (foto.id) {
      try {
        await PropertyPhotoService.deletePhoto(foto.id);
      } catch (error) {
        console.error('Erro ao deletar foto:', error);
        toast({
          title: "Erro",
          description: "Não foi possível remover a foto",
          variant: "destructive",
        });
        return;
      }
    }
    
    // Remover a foto da lista
    const novasFotos = fotos.filter((_, i) => i !== index);
    setFotos(novasFotos);
    
    // Se a foto removida era a de capa, escolher uma nova aleatoriamente
    if (isFotoCapa && novasFotos.length > 0) {
      const indiceAleatorio = Math.floor(Math.random() * novasFotos.length);
      const novaFotoCapa = novasFotos[indiceAleatorio];
      setImagemCapa(novaFotoCapa.filename || '');
      
      console.log('🔄 Foto de capa alterada automaticamente:', novaFotoCapa.filename);
      
      toast({
        title: "Foto de capa alterada",
        description: `A foto de capa foi alterada automaticamente para outra foto do imóvel.`,
      });
    }
    
    toast({
      title: "Foto removida",
      description: "A foto foi removida com sucesso.",
    });
  };

  const updateLegenda = (index: number, legenda: string) => {
    console.log('📝 Atualizando legenda:', { index, legenda });
    setFotos(prev =>
      prev.map((foto, i) => (i === index ? { ...foto, legenda } : foto))
    );
  };

  const toggleAmenidade = (amenidade: string) => {
    setAmenidadesSelecionadas(prev => 
      prev.includes(amenidade) 
        ? prev.filter(a => a !== amenidade)
        : [...prev, amenidade]
    );
  };

  if (authLoading || loading) {
    return (
      <AdminLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Carregando dados do imóvel...</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!propertyData) {
    return (
      <AdminLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Imóvel não encontrado</h1>
            <Button onClick={() => navigate('/admin/imoveis')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar para Imóveis
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Editar Imóvel
              </h1>
              <p className="text-muted-foreground">
                Atualize as informações do imóvel
              </p>
    </div>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/imoveis')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
    </div>
    </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informações Básicas */}
          <Card>
            <CardHeader>
              <CardTitle>Informações Básicas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Empreendimento
                </label>
                <Select
                  value={formData.development_id || "none"}
                  onValueChange={(value) => handleInputChange('development_id', value === "none" ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o empreendimento (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    {developments.map((development) => (
                      <SelectItem key={development.id} value={development.id.toString()}>
                        {development.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Nome do Imóvel <span className="text-destructive">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="Ex: Residencial Vista Linda"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                />
    </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Descrição <span className="text-destructive">*</span>
                </label>
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => handleInputChange('description', value)}
                  placeholder="Descreva seu imóvel, suas características e diferenciais..."
                  minHeight="200px"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Use o editor para formatar o texto com negrito, itálico, listas e mais.
                </p>
    </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Quartos
                  </label>
                  <Input 
                    type="number" 
                    placeholder="1" 
                    value={formData.bedrooms}
                    onChange={(e) => handleInputChange('bedrooms', e.target.value)}
                    min="1"
                  />
    </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Banheiros
                  </label>
                  <Input 
                    type="number" 
                    placeholder="2" 
                    value={formData.bathrooms}
                    onChange={(e) => handleInputChange('bathrooms', e.target.value)}
                    min="1"
                  />
    </div>
    </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Máximo de Hóspedes
                  </label>
                  <Input 
                    type="number" 
                    placeholder="4" 
                    value={formData.max_guests}
                    onChange={(e) => handleInputChange('max_guests', e.target.value)}
                    min="1"
                  />
    </div>
    </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Tipo de Imóvel
                  </label>
                  <Select 
                    value={formData.property_type}
                    onValueChange={(value) => handleInputChange('property_type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="apartment">Apartamento</SelectItem>
                      <SelectItem value="house">Casa</SelectItem>
                      <SelectItem value="condo">Condomínio</SelectItem>
                      <SelectItem value="studio">Studio</SelectItem>
                    </SelectContent>
                  </Select>
    </div>
                <div className="flex items-center space-x-2 pt-6">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked.toString())}
                    className="rounded"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium">
                    Imóvel Ativo
                  </label>
    </div>
    </div>
            </CardContent>
          </Card>

          {/* Regras da Casa */}
          <Card>
            <CardHeader>
              <CardTitle>Regras da Casa</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Regras
                </label>
                <RichTextEditor
                  value={formData.house_rules}
                  onChange={(value) => handleInputChange('house_rules', value)}
                  placeholder="Ex: • Check-in: 15:00 às 22:00
• Check-out: até 11:00
• Não é permitido fumar
• Não é permitido animais de estimação
• Respeite o silêncio após 22:00
• Mantenha o imóvel limpo e organizado
• Não é permitido festas ou eventos
• Use os equipamentos com cuidado
• Em caso de problemas, entre em contato conosco"
                  minHeight="200px"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Use o editor para formatar o texto com negrito, itálico, listas e mais.
                </p>
    </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Informações Adicionais
                </label>
                <RichTextEditor
                  value={formData.additional_info}
                  onChange={(value) => handleInputChange('additional_info', value)}
                  placeholder="Adicione informações adicionais sobre o imóvel..."
                  minHeight="240px"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Use o editor para formatar o texto com negrito, itálico, listas e mais.
                </p>
    </div>
            </CardContent>
          </Card>

          {/* Amenidades */}
          <Card>
            <CardHeader>
              <CardTitle>Amenidades</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {amenidades.map((amenidade) => {
                  const Icon = amenidade.icon;
                  const isSelected = amenidadesSelecionadas.includes(amenidade.id);
                  
                  return (
                    <button
                      key={amenidade.id}
                      type="button"
                      onClick={() => toggleAmenidade(amenidade.id)}
                      className={`p-4 border rounded-lg text-center transition-colors ${
                        isSelected 
                          ? 'border-primary bg-primary/10 text-primary' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <Icon className="h-6 w-6 mx-auto mb-2" />
                      <span className="text-sm font-medium">{amenidade.label}</span>
                    </button>
                  );
                })}
    </div>
            </CardContent>
          </Card>

          {/* Fotos */}
          <Card>
            <CardHeader>
              <CardTitle>Fotos do Imóvel</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Adicionar Fotos
                </label>
                <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mb-2">
                    Arraste e solte as fotos aqui ou clique para selecionar
                  </p>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e.target.files)}
                    className="hidden"
                    id="photo-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('photo-upload')?.click()}
                  >
                    Selecionar Fotos
                  </Button>
    </div>
    </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-gray-500">Carregando fotos...</p>
    </div>
              ) : fotos.length > 0 ? (
                <div>
                  <div className="mb-4 text-sm text-gray-600">
                    {fotos.length} foto(s) encontrada(s)
    </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {fotos.map((foto, index) => (
                    <div key={index} className="relative group">
                      <div className="aspect-video rounded-lg overflow-hidden border relative">
                        <img
                          src={foto.url}
                          alt={`Foto ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            console.error('Erro ao carregar imagem:', foto.url);
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        {/* Botão de excluir no canto superior direito */}
                        <Button
                          type="button"
                          variant={fotos.length === 1 ? "secondary" : "destructive"}
                          size="sm"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => {
                            if (fotos.length === 1) {
                              toast({
                                title: "Não é possível excluir",
                                description: "Esta é a única foto do imóvel e é obrigatória como foto de capa.",
                                variant: "destructive",
                              });
                              return;
                            }
                            
                            if (confirm('Tem certeza que deseja excluir esta foto?')) {
                              removeFoto(index);
                            }
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
    </div>
                      <Input
                        placeholder="Legenda da foto"
                        value={foto.legenda}
                        onChange={(e) => updateLegenda(index, e.target.value)}
                        className="mt-2"
                      />
                      <div className="mt-2">
                        <Button
                          type="button"
                          variant={imagemCapa === foto.filename ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            console.log('🖼️ Definindo imagem de capa:', foto.filename);
                            setImagemCapa(foto.filename || '');
                          }}
                          className="w-full"
                        >
                          {imagemCapa === foto.filename ? "Imagem de Capa" : "Definir como Capa"}
                        </Button>
    </div>
    </div>
                  ))}
    </div>
    </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>Nenhuma foto encontrada para este imóvel</p>
                  <p className="text-sm">Adicione fotos usando o botão acima</p>
    </div>
              )}
            </CardContent>
          </Card>

          {/* Botões de Ação */}
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/imoveis')}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminEditProperty;
